'use client';

import { c as _c } from "react/compiler-runtime";
import { PLATFORM_LABELS } from '@shared/all/crosspost';
import type { CrossPostPlatform } from '@shared/all/validators';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileVideo, Image, Type } from 'lucide-react';
import { useReducer } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import CrossPostPlatformCard from './CrossPostPlatformCard';
type Props = {
  postId: string;
  closeHandler: () => void;
};
type State = {
  selected: Set<CrossPostPlatform>;
  // null = post now; a date parks the cross-post until then
  scheduledFor: Date | null;
  submitting: boolean;
  showConfirm: boolean;
};
type Action = {
  type: 'TOGGLE_PLATFORM';
  platform: CrossPostPlatform;
} | {
  type: 'SET_SCHEDULED_FOR';
  value: Date | null;
} | {
  type: 'SET_SUBMITTING';
  value: boolean;
} | {
  type: 'SET_CONFIRM';
  value: boolean;
} | {
  type: 'RESET_SELECTED';
};
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'TOGGLE_PLATFORM':
      {
        const next = new Set(state.selected);
        if (next.has(action.platform)) {
          next.delete(action.platform);
        } else {
          next.add(action.platform);
        }
        return {
          ...state,
          selected: next
        };
      }
    case 'SET_SCHEDULED_FOR':
      return {
        ...state,
        scheduledFor: action.value
      };
    case 'SET_SUBMITTING':
      return {
        ...state,
        submitting: action.value
      };
    case 'SET_CONFIRM':
      return {
        ...state,
        showConfirm: action.value
      };
    case 'RESET_SELECTED':
      return {
        ...state,
        selected: new Set(),
        scheduledFor: null
      };
    default:
      return state;
  }
}

// datetime-local speaks local wall-clock, so toISOString (UTC) would shift the
// value by the viewer's offset. Build the string from the local getters instead.
const toLocalInputValue = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const CONTENT_TYPE_META: Record<string, {
  label: string;
  icon: typeof FileVideo;
}> = {
  video: {
    label: 'Video',
    icon: FileVideo
  },
  image: {
    label: 'Images',
    icon: Image
  },
  text: {
    label: 'Text',
    icon: Type
  }
};
const CrossPostModal = t0 => {
  const $ = _c(94);
  const {
    postId,
    closeHandler
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      selected: new Set(),
      scheduledFor: null,
      submitting: false,
      showConfirm: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, dispatch] = useReducer(reducer, t1);
  let t2;
  if ($[1] !== postId) {
    t2 = {
      queryKey: ["crossPostCompat", postId],
      queryFn: () => unwrapFromResult(client.crosspost.getCompatibility.query({
        postId
      })),
      retry: false
    };
    $[1] = postId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: compatibility,
    isLoading: loadingCompat
  } = useQuery(t2);
  let t3;
  if ($[3] !== postId) {
    t3 = {
      queryKey: ["crossPostsForPost", postId],
      queryFn: () => unwrapFromResult(client.crosspost.getCrossPostsForPost.query({
        postId
      })),
      retry: false
    };
    $[3] = postId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: crossPosts,
    isLoading: loadingCrossPosts
  } = useQuery(t3);
  let selectedArray;
  let t4;
  let t5;
  let t6;
  if ($[5] !== state.selected) {
    selectedArray = Array.from(state.selected);
    t6 = useQuery;
    t4 = "crossPostCost";
    t5 = [...selectedArray].sort().join(",");
    $[5] = state.selected;
    $[6] = selectedArray;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    selectedArray = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t5) {
    t7 = [t4, t5];
    $[10] = t4;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== selectedArray) {
    t8 = () => unwrapFromResult(client.crosspost.estimateCost.query({
      platforms: selectedArray
    }));
    $[13] = selectedArray;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = selectedArray.length > 0;
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9,
      retry: false
    };
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    data: costEstimate
  } = t6(t10);
  let t11;
  if ($[19] !== costEstimate?.breakdown) {
    t11 = new Map(costEstimate?.breakdown?.map(_temp) ?? []);
    $[19] = costEstimate?.breakdown;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const costMap = t11;
  let t12;
  if ($[21] !== crossPosts?.crossPosts) {
    t12 = new Map(crossPosts?.crossPosts?.map(_temp2) ?? []);
    $[21] = crossPosts?.crossPosts;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const crossPostMap = t12;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = platform => {
      dispatch({
        type: "TOGGLE_PLATFORM",
        platform
      });
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const handleToggle = t13;
  let t14;
  if ($[24] !== postId || $[25] !== queryClient) {
    t14 = async crossPostId => {
      const [error] = await client.crosspost.retry.mutate({
        crossPostId
      });
      if (error) {
        toast(error.message || "Retry failed");
        return;
      }
      toast("Cross-post queued for retry");
      await queryClient.invalidateQueries({
        queryKey: ["crossPostsForPost", postId]
      });
    };
    $[24] = postId;
    $[25] = queryClient;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const handleRetry = t14;
  let t15;
  if ($[27] !== postId || $[28] !== queryClient) {
    t15 = async crossPostId_0 => {
      const [error_0] = await client.crosspost.cancelScheduledCrossPost.mutate({
        crossPostId: crossPostId_0
      });
      if (error_0) {
        toast(error_0.message || "Failed to cancel");
        return;
      }
      toast("Scheduled cross-post cancelled");
      await queryClient.invalidateQueries({
        queryKey: ["crossPostsForPost", postId]
      });
      await queryClient.invalidateQueries({
        queryKey: ["crossPostCount", postId]
      });
    };
    $[27] = postId;
    $[28] = queryClient;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const handleCancelScheduled = t15;
  let t16;
  if ($[30] !== postId || $[31] !== queryClient) {
    t16 = async crossPostId_1 => {
      const [error_1] = await client.crosspost.refreshMetrics.mutate({
        crossPostId: crossPostId_1
      });
      if (error_1) {
        toast(error_1.message || "Failed to refresh metrics");
        return;
      }
      toast("Metrics updated");
      await queryClient.invalidateQueries({
        queryKey: ["crossPostsForPost", postId]
      });
    };
    $[30] = postId;
    $[31] = queryClient;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const handleRefreshMetrics = t16;
  let t17;
  if ($[33] !== postId || $[34] !== queryClient || $[35] !== selectedArray || $[36] !== state.scheduledFor) {
    t17 = async () => {
      dispatch({
        type: "SET_CONFIRM",
        value: false
      });
      dispatch({
        type: "SET_SUBMITTING",
        value: true
      });
      const [error_2] = await client.crosspost.submit.mutate({
        postId,
        platforms: selectedArray,
        ...(state.scheduledFor ? {
          scheduledFor: state.scheduledFor
        } : {})
      });
      dispatch({
        type: "SET_SUBMITTING",
        value: false
      });
      if (error_2) {
        toast(error_2.message || "Cross-post failed");
        return;
      }
      dispatch({
        type: "RESET_SELECTED"
      });
      await queryClient.invalidateQueries({
        queryKey: ["crossPostsForPost", postId]
      });
      await queryClient.invalidateQueries({
        queryKey: ["crossPostCount", postId]
      });
      await queryClient.invalidateQueries({
        queryKey: ["activeCrossPosts"]
      });
    };
    $[33] = postId;
    $[34] = queryClient;
    $[35] = selectedArray;
    $[36] = state.scheduledFor;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const handleSubmit = t17;
  const isLoading = loadingCompat || loadingCrossPosts;
  const contentType = compatibility?.contentType ?? "text";
  const contentMeta = CONTENT_TYPE_META[contentType] ?? CONTENT_TYPE_META.text;
  const ContentIcon = contentMeta.icon;
  let t18;
  if ($[38] !== compatibility?.platforms) {
    t18 = new Map(compatibility?.platforms?.map(_temp3) ?? []);
    $[38] = compatibility?.platforms;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const validationMap = t18;
  let T0;
  let T1;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[40] !== ContentIcon || $[41] !== closeHandler || $[42] !== compatibility?.platforms || $[43] !== contentMeta.label || $[44] !== costEstimate || $[45] !== costMap || $[46] !== crossPostMap || $[47] !== handleCancelScheduled || $[48] !== handleRefreshMetrics || $[49] !== handleRetry || $[50] !== isLoading || $[51] !== selectedArray.length || $[52] !== state.scheduledFor || $[53] !== state.selected || $[54] !== validationMap) {
    const available = compatibility?.platforms?.filter(p_0 => p_0.compatible && p_0.connected && !crossPostMap.has(p_0.platform)) ?? [];
    let t23;
    if ($[61] !== crossPostMap) {
      t23 = platform_0 => crossPostMap.get(platform_0)?.status === "SCHEDULED";
      $[61] = crossPostMap;
      $[62] = t23;
    } else {
      t23 = $[62];
    }
    const isScheduledPlatform = t23;
    const scheduled = compatibility?.platforms?.filter(p_1 => isScheduledPlatform(p_1.platform)) ?? [];
    const posted = compatibility?.platforms?.filter(p_2 => crossPostMap.has(p_2.platform) && !isScheduledPlatform(p_2.platform)) ?? [];
    const unavailable = compatibility?.platforms?.filter(p_3 => (!p_3.compatible || !p_3.connected) && !crossPostMap.has(p_3.platform)) ?? [];
    T1 = Modal;
    t20 = closeHandler;
    t21 = 480;
    if ($[63] !== closeHandler) {
      t22 = <ModalHeader closeHandler={closeHandler}>Cross-Post</ModalHeader>;
      $[63] = closeHandler;
      $[64] = t22;
    } else {
      t22 = $[64];
    }
    T0 = ModalContent;
    t19 = isLoading ? <div className="space-y-3 py-2">{[1, 2, 3].map(_temp4)}</div> : <div className="space-y-4"><div className="flex items-center gap-2 rounded-lg bg-background-primary px-3 py-2"><ContentIcon size={14} className="text-textSecondary" /><span className="text-xs text-textSecondary">Posting as{" "}<span className="font-medium text-textPrimary">{contentMeta.label}</span></span></div>{available.length > 0 && <div className="space-y-2"><h3 className="text-xs font-medium uppercase tracking-wider text-textSecondary">Select platforms</h3>{available.map(p_4 => <CrossPostPlatformCard key={p_4.platform} platform={p_4.platform} compatible={p_4.compatible} connected={p_4.connected} contentType={p_4.contentType} costTrons={costMap.get(p_4.platform) ?? 0} selected={state.selected.has(p_4.platform)} onToggle={handleToggle} validationIssues={validationMap.get(p_4.platform)} warning={p_4.warning} />)}</div>}{scheduled.length > 0 && <div className="space-y-2"><h3 className="text-xs font-medium uppercase tracking-wider text-textSecondary">Scheduled</h3>{scheduled.map(p_5 => <CrossPostPlatformCard key={p_5.platform} platform={p_5.platform} compatible={p_5.compatible} connected={p_5.connected} contentType={p_5.contentType} costTrons={0} selected={false} onToggle={handleToggle} crossPost={crossPostMap.get(p_5.platform)} onCancelScheduled={handleCancelScheduled} validationIssues={validationMap.get(p_5.platform)} />)}</div>}{posted.length > 0 && <div className="space-y-2"><h3 className="text-xs font-medium uppercase tracking-wider text-textSecondary">Already posted</h3>{posted.map(p_6 => <CrossPostPlatformCard key={p_6.platform} platform={p_6.platform} compatible={p_6.compatible} connected={p_6.connected} contentType={p_6.contentType} costTrons={0} selected={false} onToggle={handleToggle} crossPost={crossPostMap.get(p_6.platform)} onRetry={handleRetry} onRefreshMetrics={handleRefreshMetrics} validationIssues={validationMap.get(p_6.platform)} />)}</div>}{unavailable.length > 0 && <div className="space-y-2"><h3 className="text-xs font-medium uppercase tracking-wider text-textSecondary">Unavailable</h3>{unavailable.map(p_7 => <CrossPostPlatformCard key={p_7.platform} platform={p_7.platform} compatible={p_7.compatible} connected={p_7.connected} contentType={p_7.contentType} costTrons={0} selected={false} onToggle={handleToggle} validationIssues={validationMap.get(p_7.platform)} />)}</div>}{selectedArray.length > 0 && <div className="rounded-lg border border-ds-border-standard bg-background-primary px-4 py-3"><label htmlFor="crosspost-modal-schedule" className="mb-2 block text-xs text-textSecondary">{state.scheduledFor ? "Posts at" : "Posts immediately"}</label><div className="flex items-center gap-2"><input id="crosspost-modal-schedule" type="datetime-local" min={toLocalInputValue(new Date())} value={state.scheduledFor ? toLocalInputValue(state.scheduledFor) : ""} onChange={e => dispatch({
            type: "SET_SCHEDULED_FOR",
            value: e.target.value ? new Date(e.target.value) : null
          })} className="min-w-0 flex-1 rounded border border-ds-border-standard bg-background-secondary px-2 py-1 text-sm text-textPrimary" />{state.scheduledFor && <button type="button" className="shrink-0 rounded px-2 py-1 text-xs text-textSecondary hover:text-primary" onClick={() => dispatch({
            type: "SET_SCHEDULED_FOR",
            value: null
          })}>Post now</button>}</div></div>}{selectedArray.length > 0 && costEstimate && <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-4 py-3"><span className="text-sm text-textSecondary">Estimated cost</span><span className="text-sm font-semibold text-primary">~{costEstimate.total} Trons</span></div>}</div>;
    $[40] = ContentIcon;
    $[41] = closeHandler;
    $[42] = compatibility?.platforms;
    $[43] = contentMeta.label;
    $[44] = costEstimate;
    $[45] = costMap;
    $[46] = crossPostMap;
    $[47] = handleCancelScheduled;
    $[48] = handleRefreshMetrics;
    $[49] = handleRetry;
    $[50] = isLoading;
    $[51] = selectedArray.length;
    $[52] = state.scheduledFor;
    $[53] = state.selected;
    $[54] = validationMap;
    $[55] = T0;
    $[56] = T1;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
  } else {
    T0 = $[55];
    T1 = $[56];
    t19 = $[57];
    t20 = $[58];
    t21 = $[59];
    t22 = $[60];
  }
  let t23;
  if ($[65] !== T0 || $[66] !== t19) {
    t23 = <T0>{t19}</T0>;
    $[65] = T0;
    $[66] = t19;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== closeHandler) {
    t24 = <Button text="Cancel" variant="SECONDARY" onClick={closeHandler} />;
    $[68] = closeHandler;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  const t25 = state.submitting ? state.scheduledFor ? "Scheduling..." : "Posting..." : selectedArray.length > 0 ? `${state.scheduledFor ? "Schedule for" : "Post to"} ${selectedArray.length} platform${selectedArray.length > 1 ? "s" : ""}` : "Select platforms";
  let t26;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => dispatch({
      type: "SET_CONFIRM",
      value: true
    });
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  const t27 = selectedArray.length === 0 || state.submitting;
  let t28;
  if ($[71] !== state.submitting || $[72] !== t25 || $[73] !== t27) {
    t28 = <Button text={t25} variant="PRIMARY" onClick={t26} disabled={t27} loading={state.submitting} />;
    $[71] = state.submitting;
    $[72] = t25;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t24 || $[76] !== t28) {
    t29 = <ModalFooter>{t24}{t28}</ModalFooter>;
    $[75] = t24;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== T1 || $[79] !== t20 || $[80] !== t21 || $[81] !== t22 || $[82] !== t23 || $[83] !== t29) {
    t30 = <T1 closeHandler={t20} maxWidth={t21}>{t22}{t23}{t29}</T1>;
    $[78] = T1;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
    $[82] = t23;
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== costEstimate?.total || $[86] !== handleSubmit || $[87] !== selectedArray || $[88] !== state.scheduledFor || $[89] !== state.showConfirm) {
    t31 = state.showConfirm && <Confirmation title={state.scheduledFor ? "Confirm Schedule" : "Confirm Cross-Post"} message={`Post to ${selectedArray.map(_temp5).join(", ")}${state.scheduledFor ? ` on ${state.scheduledFor.toLocaleString()}` : ""}? Estimated cost: ~${costEstimate?.total ?? 0} Trons.`} cancelHandler={() => dispatch({
      type: "SET_CONFIRM",
      value: false
    })} confirmHandler={handleSubmit} actionButtonText={state.scheduledFor ? "Schedule" : "Cross-Post"} />;
    $[85] = costEstimate?.total;
    $[86] = handleSubmit;
    $[87] = selectedArray;
    $[88] = state.scheduledFor;
    $[89] = state.showConfirm;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== t30 || $[92] !== t31) {
    t32 = <>{t30}{t31}</>;
    $[91] = t30;
    $[92] = t31;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  return t32;
};
export default CrossPostModal;
function _temp(b) {
  return [b.platform, b.pushCostTrons];
}
function _temp2(cp) {
  return [cp.platform, cp];
}
function _temp3(p) {
  return [p.platform, (p as {
    validationIssues?: string[];
  }).validationIssues ?? []];
}
function _temp4(i) {
  return <div key={i} className="flex items-center gap-3 rounded-xl border border-ds-border-standard bg-background-secondary p-3"><div className="size-10 animate-pulse rounded-lg bg-background-primary" /><div className="flex-1 space-y-2"><div className="h-4 w-24 animate-pulse rounded bg-background-primary" /><div className="h-3 w-16 animate-pulse rounded bg-background-primary" /></div></div>;
}
function _temp5(p_8) {
  return PLATFORM_LABELS[p_8];
}